// client/src/components/Home.js

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import HomeFooter from "./HomeFooter";
import HomeHeader from "./HomeHeader";
import { HomeStyles } from "./HomeUI";
import { FiZap, FiSmartphone, FiBarChart2 } from "react-icons/fi";

function Section({
  title,
  subtitle,
  ctaLabel,
  ctaTo,
  imageUrl,
  imageAlt,
  reverse,
}) {
  return (
    <section className="lp-section">
      <div className={`lp-card lp-split${reverse ? " lp-split--reverse" : ""}`}>
        <div className="lp-split__media">
          <img src={imageUrl} alt={imageAlt} />
        </div>
        <div>
          <h2 className="lp-h2">{title}</h2>
          <ol className="lp-steps">{subtitle}</ol>
          <Link to={ctaTo} className="lp-btn lp-btn--primary">
            {ctaLabel} <span className="lp-btn__arrow" aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <li>
      <span className="lp-steps__num">{n}</span>
      <span>{children}</span>
    </li>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const storedUserRole = localStorage.getItem("userRole");
    const storedUserEmail = localStorage.getItem("userEmail");
    if (storedUserRole && storedUserEmail) {
      if (storedUserRole === "client") navigate("/client-dashboard");
      if (storedUserRole === "commercant") navigate("/merchant-dashboard");
      if (storedUserRole === "admin") navigate("/admin-dashboard");
    }

    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [navigate]);

  const features = useMemo(
    () => [
      {
        title: "Fidélité",
        desc: "Comptabilisez les passages et choisissez le gain final.",
      },
      {
        title: "Satisfaction",
        desc: "Collectez une note simple après chaque scan.",
      },
      {
        title: "Promotions",
        desc: "Publiez vos annonces et offres en quelques clics.",
      },
      {
        title: "Factures",
        desc: "Centralisez vos factures d’achats pro au même endroit.",
      },
    ],
    []
  );

  const heroImage1 =
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2Fbanniere2026-2.png?alt=media&token=cf080206-47ba-4ce6-a728-9d470c572e46";

  const heroImage2 =
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2Fbanniere2026.png?alt=media&token=553fc1e8-215e-4164-95d7-ab08d3f9d888";

  const imgClient =
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2Flogo_scanQRC.png?alt=media&token=9c34c353-2d71-4ec9-8bc3-a06d00b4cc35";

  const imgMerchant =
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2Flogo_commercants.png?alt=media&token=4c72f8d3-935d-442e-aa81-9ed5964b0753";

  // ✅ Photo top-right : URL firebase valide + rendu rectangulaire (carte)
  const founderPhoto = heroImage1;

  const clientLogos = [
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FLOGO%20CLIENTS%2F1.png?alt=media&token=1aa29a93-c3e6-4f27-b91d-58426cf0ecb0",
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FLOGO%20CLIENTS%2F3.png?alt=media&token=b0f5da13-f324-444b-853c-5bfdf1d69cff",
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FLOGO%20CLIENTS%2F2.png?alt=media&token=ee632b64-df96-43cb-b08f-8e2902f645a4",
    "https://firebasestorage.googleapis.com/v0/b/ecobill-pay.firebasestorage.app/o/Administratif%2FLOGO%20CLIENTS%2F4.png?alt=media&token=79ce1ff6-ce5f-48e7-ac75-cabee156bce3",
  ];
  const marqueeLogos = [
    ...clientLogos,
    ...clientLogos,
    ...clientLogos,
    ...clientLogos,
  ];

  return (
    <div className={`lp-page${isMobile ? " is-mobile" : ""}`}>
      <HomeStyles />
      <HomeHeader />

      <header className="lp-hero">
        <div className="lp-shell lp-hero__grid">
          <div>
            <span className="lp-eyebrow">
              <span className="lp-eyebrow__dot" aria-hidden="true" />
              Fidélité • Satisfaction • Promotions • Factures
            </span>

            <h1 className="lp-h1">
              Boostez la fidélité
              <br />
              de vos clients.
            </h1>

            <p className="lp-lead">
              Fidélité par points ou par passage, note de satisfaction,
              promotions et centralisation des notes de frais
            </p>

            <div className="lp-chips">
              <span className="lp-chip"><FiZap aria-hidden="true" /> Installation -5min</span>
              <span className="lp-chip"><FiSmartphone aria-hidden="true" /> Scan QRCode</span>
              <span className="lp-chip"><FiBarChart2 aria-hidden="true" /> Dashboard clair</span>
            </div>
          </div>

          {/* Bannière principale */}
          <div className="lp-media">
            <img src={founderPhoto} alt="Bannière Ecobill" />
          </div>
        </div>
      </header>

      <section className="lp-section lp-section--tight">
        <div className="lp-shell">
          <div className="lp-actions" style={{ justifyContent: "center", marginTop: 0, marginBottom: 28 }}>
            <Link to="/merchantofferselection" className="lp-btn lp-btn--primary">
              Pricing commerçant <span className="lp-btn__arrow" aria-hidden="true">→</span>
            </Link>
            <Link to="/homecommercant" className="lp-btn lp-btn--secondary">
              En savoir plus <span className="lp-btn__arrow" aria-hidden="true">→</span>
            </Link>
          </div>

          <p className="lp-proof">
            <strong>+3150</strong> utilisateurs nous font déjà confiance
          </p>

          <div className="lp-logos">
            <div className="ecobill-marquee">
              {marqueeLogos.map((src, idx) => (
                <div key={idx} className="lp-logo">
                  <img src={src} alt={`Logo client ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main className="lp-main">
        <div className="lp-shell">
          <section className="lp-section">
            <div className="lp-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))" }}>
              {features.map((f, i) => (
                <div key={i} className="lp-card" style={{ padding: 24 }}>
                  <h3 className="lp-h3" style={{ fontSize: "1.15rem", marginBottom: 8 }}>{f.title}</h3>
                  <p className="lp-text" style={{ fontSize: 15 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="lp-section">
            <div className="lp-media lp-media--wide">
              <img src={heroImage2} alt="Bannière Ecobill" />
            </div>
          </section>

          <Section
            title="Utilisateurs"
            subtitle={
              <>
                <Step n={1}>Scannez le QR-Code de l’entreprise</Step>
                <Step n={2}>Cumulez vos points ou vos passages</Step>
                <Step n={3}>Notez votre expérience en un clic</Step>
              </>
            }
            ctaLabel="En savoir plus"
            ctaTo="/homeclient"
            imageUrl={imgClient}
            imageAlt="Utilisateurs"
            reverse={false}
          />

          <Section
            title="Entreprises"
            subtitle={
              <>
                <Step n={1}>Fidélisez vos clients avec votre programme numérique</Step>
                <Step n={2}>Recueillez le taux de satisfaction de vos clients</Step>
                <Step n={3}>Communiquez et diffusez vos annonces</Step>
              </>
            }
            ctaLabel="En savoir plus"
            ctaTo="/homecommercant"
            imageUrl={imgMerchant}
            imageAlt="Entreprises"
            reverse={true}
          />
        </div>
      </main>

      <HomeFooter />
    </div>
  );
}
